import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Checkbox, Col, Form, Radio, Row, Skeleton, Space, Typography } from "antd";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";
import { palette } from "@/theme";

type Settings = Schemas["LabelSettings"];

/** Pengaturan > Label & Resi: how labels print (paper, what Zerpid adds around the courier label). */
export function LabelSettings() {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Settings>();
  const watched = Form.useWatch([], form) as Settings | undefined;

  const current = useQuery({ queryKey: ["settings", "labels"], queryFn: () => unwrap(api.GET("/v1/settings/labels")) });
  const save = useMutation({
    mutationFn: (v: Settings) => unwrap(api.PUT("/v1/settings/labels", { body: v })),
    onSuccess: (v) => {
      queryClient.setQueryData(["settings", "labels"], v);
      void message.success(t("settings.labels.saved"));
    },
    onError: (err) => void message.error(errorText(err)),
  });

  const s = watched ?? current.data;
  return (
    <>
      <Typography.Title level={2} style={{ marginTop: 0, fontSize: 28 }}>
        {t("settings.labels.title")}
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ maxWidth: 720 }}>
        {t("settings.labels.intro")}
      </Typography.Paragraph>
      {!current.data ? (
        <Skeleton active />
      ) : (
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={14}>
            <Card>
              <Form form={form} layout="vertical" initialValues={current.data} onFinish={(v) => save.mutate(v)} disabled={save.isPending}>
                <Form.Item name="paper" label={t("settings.labels.paper")}>
                  <Radio.Group style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <Radio value="thermal">
                      <Space orientation="vertical" size={0}>
                        <span>{t("settings.labels.paper.thermal")}</span>
                        <Typography.Text type="secondary">{t("settings.labels.paper.thermalHelp")}</Typography.Text>
                      </Space>
                    </Radio>
                    <Radio value="a4">
                      <Space orientation="vertical" size={0}>
                        <span>{t("settings.labels.paper.a4")}</span>
                        <Typography.Text type="secondary">{t("settings.labels.paper.a4Help")}</Typography.Text>
                      </Space>
                    </Radio>
                  </Radio.Group>
                </Form.Item>
                <Typography.Text strong>{t("settings.labels.additions")}</Typography.Text>
                <Form.Item name="showTracking" valuePropName="checked" extra={t("settings.labels.showTrackingHelp")} style={{ marginTop: 8, marginBottom: 12 }}>
                  <Checkbox>{t("settings.labels.showTracking")}</Checkbox>
                </Form.Item>
                <Form.Item name="showOrderInfo" valuePropName="checked" style={{ marginBottom: 12 }}>
                  <Checkbox>{t("settings.labels.showOrderInfo")}</Checkbox>
                </Form.Item>
                <Form.Item name="showItems" valuePropName="checked" extra={t("settings.labels.showItemsHelp")}>
                  <Checkbox>{t("settings.labels.showItems")}</Checkbox>
                </Form.Item>
                <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                  {t("settings.labels.shrinkNote")}
                </Typography.Paragraph>
                <Button type="primary" htmlType="submit" loading={save.isPending}>
                  {t("common.save")}
                </Button>
              </Form>
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card title={t("settings.labels.preview")}>{s && <Preview s={s} />}</Card>
          </Col>
        </Row>
      )}
    </>
  );
}

/** A schematic of the printed label, true to the 100×150 proportions. */
function Preview({ s }: { s: Settings }) {
  const one = <LabelSketch s={s} />;
  if (s.paper !== "a4") return <div style={{ width: 220, margin: "0 auto" }}>{one}</div>;
  return (
    <div style={{ width: 280, aspectRatio: "210 / 297", margin: "0 auto", border: "1px solid var(--z-line)", padding: 6, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
      {[0, 1, 2, 3].map((i) => (
        <div key={i}>{one}</div>
      ))}
    </div>
  );
}

function LabelSketch({ s }: { s: Settings }) {
  const { t } = useI18n();
  const small = s.paper === "a4";
  const bar = (w: string, h = 4) => <div style={{ width: w, height: h, borderRadius: 2, background: "var(--z-muted)", opacity: 0.45 }} />;
  return (
    <div style={{ aspectRatio: "100 / 150", border: "1px solid var(--z-ink)", background: palette.putih, display: "flex", flexDirection: "column", padding: small ? 3 : 8, gap: small ? 2 : 6 }}>
      {s.showTracking && (
        <div style={{ textAlign: "center" }}>
          <div className="z-num" style={{ fontWeight: 700, fontSize: small ? 8 : 16, letterSpacing: 0.5, lineHeight: 1.1 }}>
            SPXID0123456789
          </div>
          <div
            style={{
              height: small ? 6 : 16,
              margin: small ? "2px 6px 0" : "4px 14px 0",
              background: "repeating-linear-gradient(90deg, var(--z-ink) 0 2px, transparent 2px 3px, var(--z-ink) 3px 4px, transparent 4px 7px)",
            }}
          />
        </div>
      )}
      {s.showOrderInfo && (
        <div className="z-num" style={{ fontSize: small ? 5 : 9, color: "var(--z-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          251011ABC · Shopee Toko Sinar · SPX Express
        </div>
      )}
      {(s.showTracking || s.showOrderInfo) && <div style={{ borderTop: "1px solid var(--z-ink)" }} />}
      <div
        style={{
          flex: 1,
          border: "1px dashed var(--z-muted)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          fontSize: small ? 5 : 10,
          color: "var(--z-muted)",
          padding: 4,
          margin: s.showTracking || s.showOrderInfo || s.showItems ? "0 6%" : 0,
        }}
      >
        {t("settings.labels.previewLabel")}
      </div>
      {s.showItems && (
        <>
          <div style={{ borderTop: "1px solid var(--z-ink)" }} />
          <Space orientation="vertical" size={small ? 1 : 3} style={{ width: "100%" }}>
            {bar("80%", small ? 2 : 4)}
            {bar("65%", small ? 2 : 4)}
            {bar("50%", small ? 2 : 4)}
          </Space>
        </>
      )}
    </div>
  );
}
